import { useState } from 'react';
import { Download, FileSpreadsheet, Package, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError, downloadUrl } from '@/api/client';
import { useDeleteMedicine, useMedicines } from '@/api/hooks/useMedicine';
import type { Medicine } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { AdjustStockDialog } from '@/features/inventory/AdjustStockDialog';
import { BulkImportDialog } from '@/features/inventory/BulkImportDialog';
import { MedicineFormDialog } from '@/features/inventory/MedicineFormDialog';
import { MedicineRow, MedicineRowSkeletonList } from '@/features/inventory/MedicineRow';
import { RestockDialog } from '@/features/inventory/RestockDialog';
import { SubstitutesDialog } from '@/features/inventory/SubstitutesDialog';
import { InventoryFilters } from '@/features/inventory/InventoryFilters';

type DialogKind = 'create' | 'edit' | 'restock' | 'adjust' | 'substitutes' | 'delete' | 'import' | null;

export function InventoryPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const status = params.get('status') ?? '';
  const category = params.get('category') ?? '';
  const page = Number(params.get('page') ?? '1');

  const debouncedSearch = useDebouncedValue(search, 300);

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [activeMedicine, setActiveMedicine] = useState<Medicine | null>(null);

  const deleteMedicine = useDeleteMedicine();

  const { data, isLoading, isFetching } = useMedicines({
    search: debouncedSearch || undefined,
    status: status || undefined,
    category: category || undefined,
    page,
    limit: 30,
  });

  function updateParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  }

  function openDialog(kind: DialogKind, medicine: Medicine | null = null) {
    setActiveMedicine(medicine);
    setDialog(kind);
  }

  function closeDialog() {
    setDialog(null);
    setActiveMedicine(null);
  }

  async function handleDelete() {
    if (!activeMedicine) return;
    try {
      await deleteMedicine.mutateAsync(activeMedicine.id);
      toast({ title: 'Medicine deleted', variant: 'success' });
      closeDialog();
    } catch (error) {
      toast({
        title: 'Could not delete',
        description: error instanceof ApiError ? error.message : undefined,
        variant: 'destructive',
      });
    }
  }

  const medicines = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">{t('medicinesTitle')}</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} medicine(s)` : 'Loading…'}
          </p>
        </div>
        <div className="flex gap-2">
          <Can permission={PERMISSIONS.medicinesBulkImport}>
            <Button variant="outline" onClick={() => openDialog('import')}>
              <FileSpreadsheet /> Import
            </Button>
          </Can>
          <Can permission={PERMISSIONS.exportsCreate}>
            <Button variant="outline" asChild>
              <a href={downloadUrl('/api/export/stock')}>
                <Download /> XLSX
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={downloadUrl('/api/export/stock/csv')}>
                <Download /> CSV
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href={downloadUrl('/api/export/stock/pdf')}>
                <Download /> PDF
              </a>
            </Button>
          </Can>
          <Can permission={PERMISSIONS.medicinesCreate}>
            <Button onClick={() => openDialog('create')}>
              <Plus /> {t('addMedicine')}
            </Button>
          </Can>
        </div>
      </div>

      <BulkImportDialog open={dialog === 'import'} onOpenChange={(v) => setDialog(v ? 'import' : null)} />

      <InventoryFilters
        search={search}
        onSearchChange={(v) => updateParam('search', v)}
        status={status}
        onStatusChange={(v) => updateParam('status', v)}
        category={category}
        onCategoryChange={(v) => updateParam('category', v)}
      />

      {isLoading ? (
        <MedicineRowSkeletonList />
      ) : medicines.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Package className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search || status || category
                ? 'No medicines match these filters.'
                : 'No medicines yet — add your first one to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" aria-busy={isFetching}>
          {medicines.map((medicine) => (
            <MedicineRow
              key={medicine.id}
              medicine={medicine}
              onEdit={() => openDialog('edit', medicine)}
              onRestock={() => openDialog('restock', medicine)}
              onAdjust={() => openDialog('adjust', medicine)}
              onSubstitutes={() => openDialog('substitutes', medicine)}
              onDelete={() => openDialog('delete', medicine)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => updateParam('page', String(page - 1))}
          >
            Previous
          </Button>
          <span className="tabular text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => updateParam('page', String(page + 1))}
          >
            Next
          </Button>
        </div>
      )}

      <MedicineFormDialog
        medicine={dialog === 'edit' ? (activeMedicine ?? undefined) : undefined}
        open={dialog === 'create' || dialog === 'edit'}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <RestockDialog
        medicine={dialog === 'restock' ? activeMedicine : null}
        open={dialog === 'restock'}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <AdjustStockDialog
        medicine={dialog === 'adjust' ? activeMedicine : null}
        open={dialog === 'adjust'}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <SubstitutesDialog
        medicine={dialog === 'substitutes' ? activeMedicine : null}
        open={dialog === 'substitutes'}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(open) => !open && closeDialog()}
        title={`Delete ${activeMedicine?.name ?? 'this medicine'}?`}
        description="It will no longer appear in inventory or billing. Past sales that included it are unaffected."
        confirmLabel={t('delete')}
        pending={deleteMedicine.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}